// The one model pass between the idea and the build.
//
// It REFINES a scaffold that is already on screen rather than producing a plan from
// nothing, so the user is reading and editing a real plan while this runs. Every failure
// short of a credit denial returns the scaffold unchanged: the wizard has nothing to
// recover from and nothing to explain.

import { KITS } from '@/webapp/runtime/kits';
import type { AppPlan } from './planTypes';
import { CHART_KINDS, normalizePlan } from './planTypes';
import { scaffoldFor } from './scaffolds';

export interface PlanIO {
  generate(input: {
    systemPrompt: string;
    messages: { role: 'user' | 'assistant'; content: string }[];
    signedIn: boolean;
    structured?: boolean;
  }): Promise<{ content: string }>;
}

export interface PlanOutcome {
  /** Always a complete, buildable plan — the scaffold when refinement did not land. */
  plan: AppPlan;
  /** True when the model's answer was used. */
  refined: boolean;
  /** Non-null when the call failed. The caller routes credit/auth denials from here. */
  error: unknown | null;
}

export function buildPlanPrompt(): string {
  const kits = KITS.map((k) => `- ${k.id}: ${k.when}`);
  return [
    'You plan small mobile web apps before they are built. You are given a rough idea and a',
    'starting plan. Return an IMPROVED plan as JSON — the user will read it, edit it, and',
    'then it is built exactly as they leave it.',
    '',
    'Return ONLY a JSON object. No prose, no fences. Shape:',
    '{',
    '  "appName": "short, specific, in the user\'s language — not a category",',
    '  "tagline": "one line saying what it does for the person using it",',
    '  "kitId": "one of the ids below",',
    '  "features": [{ "label": "3-5 words", "detail": "one line the builder can act on", "included": true }],',
    '  "screens":  [{ "label": "one word if possible", "purpose": "one line" }],',
    `  "charts":   [{ "label": "…", "kind": "${CHART_KINDS.join('|')}", "source": "what it is drawn from", "included": true }],`,
    '  "seedNote": "what the sample content should be",',
    '  "constraints": [{ "label": "short rule", "detail": "why it applies", "workaround": "what to do INSTEAD", "kind": "legal|safety|privacy|accuracy|cost" }],',
    '  "rules": { "capacity": { "groupBy": "field", "limit": 20 }, "window": { "closesAt": "ISO date" }, "uniqueBy": "email", "overflow": "reject|waitlist", "countBy": "field" },',
    '  "manages": [ { "collection": "shifts", "fields": ["name", "startsAt", "needed"] } ]',
    '}',
    '',
    'DESIGN KITS — choose by subject, not by taste:',
    ...kits,
    '',
    'RULES:',
    '- 5 to 8 features. Mark the 4-6 that make the app worth having as included:true, and',
    '  leave genuinely optional ones included:false — the user sees both and can flip either.',
    '- Features must be SPECIFIC TO THIS IDEA. "Add an entry" is a stub; "Log a set with',
    '  weight and reps" is a feature. Name what this app in particular does.',
    '- Rewrite the starting plan where the idea is more specific than it is. Keep what fits.',
    '- 2 to 4 screens. One of them must be where the app opens.',
    '- Charts only where the data is the app\'s own and readable. An app that collects from',
    '  visitors can never read its collection back, so it gets NO charts and NO counts.',
    '- Seed notes describe realistic content, dated relative to today, never hardcoded dates.',
    '',
    `TODAY IS ${new Date().toISOString().slice(0, 10)}. Every date you produce must be after it.`,
    '',
    'RULES — only for an app that collects. These are enforced by the SERVER, because the',
    'page is editable by anyone holding the link, so a limit written in its JavaScript is a',
    'suggestion. Take the NUMBERS FROM THE IDEA: "twenty seats a night" is',
    'capacity {groupBy:"nightId", limit:20}; "reply by the 12th" is a window closesAt — the NEXT',
    '12th, not one that has gone; "one plot per member" is uniqueBy. Set countBy to the same',
    'field as capacity.groupBy so the page can honestly say how many places are left.',
    'Every field a rule names — groupBy, uniqueBy, countBy — must be one of the fields in',
    '`collects`, or the rule keys on something the visitor never sends and quietly does',
    'nothing. Omit anything the idea does not state —',
    'a limit nobody asked for is worse than none. Omit `rules` entirely for an app that',
    'collects nothing.',
    '',
    'MANAGES — collections the OWNER keeps up to date after the app is published, which',
    'visitors read. Anything the person says THEY will post, add or change: "I post the',
    'week\'s shifts", "I add new sessions", "this week\'s route". These live on the server, so',
    'the owner changes them from Saddle and every visitor sees it without the app being',
    'rebuilt — which is the whole point of them.',
    'Name the fields, because the owner types these rows in by hand and a column they cannot',
    'guess is one they cannot fill. Use a date field ending "At" for anything dated.',
    'Two collections at most, for a parent and its children (a class and its sessions).',
    'ONLY things the owner writes. If anyone other than the owner adds a row to it — a',
    'booking, a claim, a swap request, a reply — it is not this: it belongs in `collects`,',
    'and putting it here makes the server refuse the very people it was meant to serve.',
    'Leave it out entirely for an app whose content never changes after it is built.',
    '',
    'CONSTRAINTS — the part people forget until it is too late, and the creative way round it.',
    'Every constraint needs a `workaround`: what the app does INSTEAD. A rule that only forbids',
    'leaves a hole in the app; a rule that substitutes makes it better. "No picture of a prophet"',
    'is a hole. "Show the place — the desert road, the split sea, the lamp — or the geometry of',
    'Islamic art" is an app. Reach for the substitution first and refuse only when nothing works.',
    'Name anything this subject in',
    'particular obliges the builder to respect, that the person asking would not think to say.',
    'Nothing generic: no "be accessible", no "handle errors". Only what THIS app carries. If it',
    'genuinely carries none, return an empty list. Examples of the real thing:',
    '- A scripture reader may ship only public-domain translations, and must not depict the',
    '  Prophet Muhammad or any prophet — a hard rule in Islam that most traditions extend to all',
    '  prophets, and a live sensitivity for depictions of God or Jesus too.',
    '- A training app prescribes load to real bodies: cap progression, never programme through',
    '  reported pain, and say it is not a coach or clinician.',
    '- A condition or damage report is one party\'s evidence, not a neutral verdict.',
    '- Anything holding health, children\'s or identity data says what it keeps and why.',
    '- An app generating an image per item has a cost per person that someone has to bear.',
  ].join('\n');
}

export async function planApp(idea: string, io: PlanIO, signedIn: boolean): Promise<PlanOutcome> {
  const scaffold = scaffoldFor(idea);
  const user = [
    `Idea: ${idea}`,
    '',
    'Starting plan:',
    JSON.stringify(
      {
        appName: scaffold.appName,
        kitId: scaffold.kitId,
        features: scaffold.features.map((f) => ({ label: f.label, detail: f.detail, included: f.included })),
        screens: scaffold.screens.map((s) => ({ label: s.label, purpose: s.purpose })),
        charts: scaffold.charts.map((c) => ({ label: c.label, kind: c.kind, source: c.source })),
        collects: scaffold.collects,
      },
      null,
      1,
    ),
    '',
    scaffold.collects
      ? `This app COLLECTS from visitors into "${scaffold.collects.collection}". No charts, no counts.`
      : 'This app keeps its data on the visitor\'s own device.',
    '',
    'Return the improved plan as JSON now.',
  ].join('\n');

  try {
    // `structured: true` is load-bearing, not decoration: without it the proxy routes to
    // the cheap chat model at a 1,500-token cap — the capacity signature that produced
    // uniformly shallow output on the mini-app path.
    const { content } = await io.generate({
      systemPrompt: buildPlanPrompt(),
      messages: [{ role: 'user', content: user }],
      signedIn,
      structured: true,
    });
    const plan = normalizePlan(content, scaffold);
    return { plan, refined: plan !== scaffold, error: null };
  } catch (error) {
    return { plan: scaffold, refined: false, error };
  }
}
